import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import type { EditModeStorageSaturationUsage } from '@connect-services/client/services/edit-mode-storage-saturation-modal.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export enum EditModeStorageSaturationAction {
    CANCEL = 'cancel',
    CLEAR_HISTORY = 'clear_history',
    SAVE = 'save',
}

export type EditModeStorageSaturationModalData = {
    canClearHistory: boolean;
    hasMultipleEditSessions: boolean;
    storageUsage: EditModeStorageSaturationUsage;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ComplexModalTemplate, RessourcesModule],
    selector: 'app-edit-mode-storage-saturation-modal',
    templateUrl: './edit-mode-storage-saturation-modal.component.html',
})
export class EditModeStorageSaturationModalComponent implements OnInit {
    private static readonly bytesPerMegabyte = 1024 * 1024;

    public descriptionKey!: string;
    public storageUsageParams!: {
        estimatedQuota: string;
        percentUsed: number;
        usedStorage: string;
    };
    public titleKey!: string;

    protected readonly ButtonType = ButtonType;
    protected readonly data: EditModeStorageSaturationModalData = inject(MAT_DIALOG_DATA);
    protected readonly dialogRef = inject(
        MatDialogRef<EditModeStorageSaturationModalComponent, EditModeStorageSaturationAction>,
    );

    public ngOnInit(): void {
        this.descriptionKey = this.data.canClearHistory
            ? 'editMode.storage.clearHistory.description'
            : 'editMode.storage.saveRequired.description';
        this.storageUsageParams = {
            estimatedQuota: this.formatBytes(this.data.storageUsage.estimatedQuotaBytes),
            percentUsed: Math.round(this.data.storageUsage.percentUsed),
            usedStorage: this.formatBytes(this.data.storageUsage.usedBytes),
        };
        this.titleKey = this.data.canClearHistory
            ? 'editMode.storage.clearHistory.title'
            : 'editMode.storage.saveRequired.title';
    }

    protected cancel(): void {
        this.dialogRef.close(EditModeStorageSaturationAction.CANCEL);
    }

    protected clearHistory(): void {
        this.dialogRef.close(EditModeStorageSaturationAction.CLEAR_HISTORY);
    }

    protected save(): void {
        this.dialogRef.close(EditModeStorageSaturationAction.SAVE);
    }

    private formatBytes(bytes: number): string {
        if (bytes >= EditModeStorageSaturationModalComponent.bytesPerMegabyte) {
            return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(
                bytes / EditModeStorageSaturationModalComponent.bytesPerMegabyte,
            )} MB`;
        }

        return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(bytes / 1024)} KB`;
    }
}
